// components/Game.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRadixConnect } from '../radix_connect';
import { useAuth, useGameData } from '../backend';
import CyberpunkOffice from './Office/CyberpunkOffice';
// Removed imports for deleted components
import '../styles/Game.scss';

// Income Notification Component
const IncomeNotification = ({ income, onClose }) => {
  useEffect(() => {
    // Auto-close after 5 seconds
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  return (
    <div className="income-notification">
      <div className="notification-content">
        <div className="notification-header">
          <h3>Passive Income</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="notification-body">
          <p>Your mining rigs generated:</p>
          <p className="income-amount">+${income.toLocaleString()} XRDEEZ</p>
          <p className="income-note">while you were away!</p>
        </div>
      </div>
    </div>
  );
};

// Money Display Component that updates independently from the main game
const MoneyDisplay = React.memo(({ money }) => {
  return (
    <div className="money-display">
      <span className="money-value">${money.toLocaleString()}</span>
    </div>
  );
});

MoneyDisplay.displayName = 'MoneyDisplay';

// Error boundary component to catch rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', color: 'white', background: 'rgba(0,0,0,0.8)' }}>
          <h2>Something went wrong.</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>Error Details</summary>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

const Game = () => {
  const { saveId } = useParams();
  const navigate = useNavigate();
  const { connected, accounts, selectedAccount } = useRadixConnect();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIncomeNotification, setShowIncomeNotification] = useState(false);
  const [incomeAmount, setIncomeAmount] = useState(0);
  const [currentMoney, setCurrentMoney] = useState(0);
  const [moneyDisplayKey, setMoneyDisplayKey] = useState(Date.now());

  console.log("Game component rendering with saveId:", saveId);

  // Derive wallet state from accounts
  const wallet = useMemo(() => ({
    connected,
    address: selectedAccount?.address || (accounts && accounts.length > 0 ? accounts[0].address : null),
    accounts
  }), [connected, selectedAccount, accounts]);

  console.log("Wallet state:", wallet);

  // Use auth and game data hooks
  const { 
    userId, 
    isAuthenticated, 
    isLoading: authLoading 
  } = useAuth(wallet);
  
  console.log("Auth state:", { userId, isAuthenticated, authLoading });
  
  const { 
    gameSave, 
    isLoading: gameLoading, 
    updateGameData, 
    lastIncomeUpdate 
  } = useGameData(userId);

  console.log("Game data state:", { 
    hasGameSave: !!gameSave, 
    gameSaveId: gameSave?.save_id,
    gameLoading, 
    hasLastIncomeUpdate: !!lastIncomeUpdate 
  });

  // Force refresh of money display
  const refreshMoneyDisplay = useCallback(() => {
    // Use the latest game save data for money
    if (gameSave && typeof gameSave.money === 'number') {
      setCurrentMoney(gameSave.money);
      
      // Change key to force re-render
      setMoneyDisplayKey(Date.now());
    }
  }, [gameSave, currentMoney]);

  // Update current money when game data changes
  useEffect(() => {
    if (gameSave && typeof gameSave.money === 'number') {
      setCurrentMoney(gameSave.money);
    }
  }, [gameSave]);

  // Check wallet connection and authentication
  useEffect(() => {
    console.log("Checking wallet and auth:", { connected: wallet.connected, isAuthenticated, authLoading });
    
    // Only check after auth loading is complete
    if (authLoading) {
      console.log("Auth still loading, not checking yet");
      return;
    }
    
    if (!wallet.connected) {
      console.error('Wallet not connected, redirecting to home');
      setError('Wallet not connected');
      navigate('/');
      return;
    }
    
    if (!isAuthenticated) {
      console.error('Not authenticated, redirecting to home');
      setError('Not authenticated');
      navigate('/');
      return;
    }
  }, [wallet.connected, isAuthenticated, navigate, authLoading]);

  // Check if we have the correct save
  useEffect(() => {
    console.log("Checking save:", { gameLoading, gameSave, saveId });
    
    // Only check after game data loading is complete
    if (gameLoading) {
      console.log("Game data still loading, not checking save yet");
      return;
    }
    
    if (!gameSave) {
      console.error('No game save found, redirecting to home');
      setError('Game save not found');
      navigate('/');
      return;
    }
    
    if (gameSave.save_id !== saveId) {
      console.error('Invalid game save, redirecting to home');
      setError('Invalid game save');
      navigate('/');
    }
  }, [gameLoading, gameSave, saveId, navigate]);

  // Show income notification if we received passive income
  useEffect(() => {
    if (lastIncomeUpdate?.income > 0) {
      setIncomeAmount(lastIncomeUpdate.income);
      setShowIncomeNotification(true);
    }
  }, [lastIncomeUpdate]);

  // Handle scene loaded callback - directly set loading to false
  const handleSceneLoaded = useCallback(() => {
    console.log("Scene loaded callback triggered, setting loading to false");
    setLoading(false);
    // Immediately update loading state if we have game data
    // This check is less critical now but doesn't hurt
    if (gameSave) {
      console.log("Game data available, ensuring loading is false");
    }
  }, [gameSave]); // Added gameSave dependency for safety, though setLoading doesn't directly use it

  // If we have game data but no loading state has been set yet, show loading
  // This effect now primarily acts as a failsafe timeout
  useEffect(() => {
    console.log("Loading effect triggered:", { gameSave: !!gameSave, loading });

    if (gameSave && loading) {
      // Set a timer to ensure we don't get stuck in loading
      const timer = setTimeout(() => {
        if (loading) { // Check again in case handleSceneLoaded was faster
          console.log("Forcing loading to false after timeout");
          setLoading(false);
        }
      }, 8000); // Increased timeout slightly

      return () => clearTimeout(timer);
    }
    // Removed sceneLoaded dependency
  }, [gameSave, loading]);

  // Handle back to main menu
  const handleBackToMainMenu = () => {
    localStorage.setItem('skipRedirect', 'true');
    navigate('/');
    setTimeout(() => {
      localStorage.removeItem('skipRedirect');
    }, 1000);
  };

  // Handle updating game data in Supabase
  const handleUpdateGameData = (updatedData) => {
    if (gameSave) {
      // Create a deep copy of the current game data
      const currentData = JSON.parse(JSON.stringify(gameSave.data));
      const newData = JSON.parse(JSON.stringify(updatedData.data));
      
      // Get game constants from the backend (or use defaults if not provided)
      const gameConstants = gameSave.constants || {};
      const MAX_RIGS = gameConstants.MAX_RIGS || 4;
      const COST_PER_RIG = gameConstants.COST_PER_RIG || 1000;
      
      // Check if only mining rig or money changed
      const isMiningRigUpdate = 
        newData.miningRigs !== currentData.miningRigs ||
        newData.money !== currentData.money;
      
      // Validate money changes - this is now primarily a UI safeguard
      // The backend will enforce stricter validation
      if (typeof newData.money === 'number') {
        // Prevent negative money values
        if (newData.money < 0) {
          console.error("Invalid money update: negative values not allowed");
          newData.money = currentData.money;
        }
      }
      
      // Validate mining rig changes - this is now primarily a UI safeguard
      // The backend will enforce stricter validation
      if (typeof newData.miningRigs === 'number') {
        // Validate mining rig count
        if (newData.miningRigs < 0 || newData.miningRigs > MAX_RIGS) {
          console.error(`Invalid mining rig update: ${newData.miningRigs}`);
          newData.miningRigs = currentData.miningRigs;
        }
        
        // Validate cost if increasing rigs
        if (newData.miningRigs > currentData.miningRigs) {
          const rigsAdded = newData.miningRigs - currentData.miningRigs;
          const expectedCost = rigsAdded * COST_PER_RIG;
          const actualMoneyDecrease = currentData.money - newData.money;
          
          // Check if money decrease matches expected cost
          const tolerance = 1; // Small tolerance for floating point errors
          if (Math.abs(actualMoneyDecrease - expectedCost) > tolerance) {
            console.error(`Mining rig cost mismatch: expected ${expectedCost}, got ${actualMoneyDecrease}`);
            // Revert both values to ensure consistent state
            newData.miningRigs = currentData.miningRigs;
            newData.money = currentData.money;
          }
        }
      }
      
      // Only update if there are actual changes and validation passed
      if (JSON.stringify(currentData) !== JSON.stringify(newData)) {
        // For mining rig updates, use a quieter update approach
        if (isMiningRigUpdate) {
          // Update game save reference directly without triggering re-renders
          // This still saves to database but avoids re-rendering the 3D world
          gameSave.data = { ...gameSave.data, ...newData };
          gameSave.money = updatedData.money;
          
          // Update money display when mining rig updates affect money
          if (typeof updatedData.money === 'number') {
            setCurrentMoney(updatedData.money);
            
            // Force a re-render of the MoneyDisplay by changing its key
            setMoneyDisplayKey(Date.now());
          }
          
          // Update to database immediately to ensure persistence
          updateGameData({
            ...newData,
            money: updatedData.money,
            mining_power: updatedData.mining_power
          });
          
          // Schedule another refresh after a short delay to ensure the display is updated
          setTimeout(refreshMoneyDisplay, 500);
        } else {
          // For other updates, use normal update
          updateGameData(newData);
        }
      }
    }
  };

  // Close income notification
  const handleCloseIncomeNotification = () => {
    setShowIncomeNotification(false);
  };

  // Loading states
  if (authLoading || gameLoading) {
    return (
      <div className="game-container">
        <div className="game-loading">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading Game Data...</div>
        </div>
      </div>
    );
  }

  // This condition should now cover both waiting for game data AND scene loading
  if (loading || !gameSave) {
    // Display error if gameSave is missing after loading checks
    if (!gameSave && !gameLoading && !authLoading) {
       return (
         <div className="game-container">
           <div className="game-loading">
             <div className="loading-text">Error loading game data</div>
             <div className="loading-subtext">{error || "Game save not found"}</div>
           </div>
         </div>
       );
    }
    // Otherwise, show the main loading screen
    return (
      <div className="game-container">
        <div className="game-loading">
          <div className="loading-spinner"></div>
          {/* Updated loading text */}
          <div className="loading-text">Loading Cyberpunk Office...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <MoneyDisplay key={moneyDisplayKey} money={currentMoney} />
      
      <div className="game-content">
        <ErrorBoundary>
          <CyberpunkOffice 
            onSceneLoaded={handleSceneLoaded} 
            gameData={gameSave}
            updateGameData={handleUpdateGameData}
            refreshMoneyDisplay={refreshMoneyDisplay}
          />
        </ErrorBoundary>
      </div>
      
      {showIncomeNotification && (
        <IncomeNotification 
          income={incomeAmount} 
          onClose={handleCloseIncomeNotification} 
        />
      )}
    </div>
  );
};

export default Game;